/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {useCallback} from "react";
import {useSafeState} from "./useSafeState";
import {useDebouncedEffect} from "./EffectHooks";

export const useDebouncedIsValidNewOption = (callback, inputValue, delay) => {
    
    const cleanInput = inputValue ?? '';
    
    const [previousInputValue, setPreviousInputValue] = useSafeState(null);
    const [isNewOptionValid, setIsNewOptionValid] = useSafeState(false);
    
    // 2. Debounced effect rechecks the validity of the new option
    //    after the specified delay, whenever the input dependencies change
    // 3. After the asynchronous check completes,
    //    it updates the state with the result of the validation.
    useDebouncedEffect(() => {
        void (async () => {
            // Handle empty input case
            if(cleanInput === '') {
                setIsNewOptionValid(false);
                return;
            }
            
            // Update previous input if changed
            if(previousInputValue !== inputValue) {
                setPreviousInputValue(inputValue);
            }
            
            try {
                const isValid = await callback(cleanInput);
                setIsNewOptionValid(isValid);
            } catch (e) {
                console.error("Unexpected error:", e);
                setIsNewOptionValid(false); // this is just to be safe, since it should already be false
            }
        })();
    }, [inputValue], delay);
    
    // 1. Called by the component to check if a new option is valid.
    //    If the input hasn't changed it returns the previous validation value
    //    Otherwise, it (temporarily) returns the false since the value
    //    has not been validated yet.
    // 5. The useCallback hook is used to memoize the function,
    //    but allows the function to be recreated when isNewOptionValid
    //    changes, ensuring the react-select component will call this
    //    function again to recheck the validity of the new option
    //    after the asynchronous validation completes.
    const queueNewValueForValidation = useCallback((newInputValue) => {
        if(newInputValue === previousInputValue) {
            return isNewOptionValid;
        }
        return false;
    }, [isNewOptionValid, previousInputValue]);
    
    const isLoading = cleanInput === '' ? false : previousInputValue !== inputValue;
    
    return [queueNewValueForValidation, isLoading];
};
